import React, { useState, useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import BlankImg from "../assets/images/blank.jpg";
import PDF from "../assets/images/pdf.png";
import { confirmAlert } from "react-confirm-alert";
const SUPPORTED_IMAGE_FORMATS = ["image/jpeg", "image/png"];
const SUPPORTED_PLAN_FORMATS = ["image/jpeg", "image/png"];

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const ImageWithFallbackSrc = React.memo(
  ({ src, alt, fallbackSrc, ...props }) => {
    const handleError = (event) => {
      event.target.src = fallbackSrc;
    };

    return <img src={src} alt={alt} onError={handleError} {...props} />;
  }
);

export const ImageWithFallback = ({ src, alt, fallbackSrc, ...props }) => {
  const [imgSrc, setImgSrc] = useState(fallbackSrc);

  useEffect(() => {
    const fetchBlobData = async () => {
      if (typeof src === "string" && src.startsWith("blob:")) {
        try {
          const response = await fetch(src);
          const blob = await response.blob();
          const mimeType = blob.type;

          // Check if it's an image
          if (mimeType.startsWith("image/")) {
            setImgSrc(URL.createObjectURL(blob));
          } else {
            // "";
            setImgSrc(PDF); // Default to fallback if not an image
          }
        } catch (error) {
          console.error("Error fetching Blob:", error);
          setImgSrc(fallbackSrc);
        }
      } else if (typeof src === "string" && src.trim() !== "") {
        // Handle string URLs directly
        setImgSrc(src);
      } else {
        // Use fallback if src is not a valid URL or Blob
        setImgSrc(fallbackSrc);
      }
    };

    fetchBlobData();
  }, [src, fallbackSrc]);

  const handleError = (event) => {
    event.target.src = fallbackSrc;
  };

  return <img src={imgSrc} alt={alt} onError={handleError} {...props} />;
};

export const DragDropImgCard = ({
  src,
  index,
  moveImage,
  setFieldValue,
  values,
  field,
  setValue,
  error = undefined,
}) => {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: "image",
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveImage(dragIndex, hoverIndex, setFieldValue, values, field, setValue);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: "image",
    item: () => {
      return { index };
    },
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  return (
    <div ref={ref} style={{ opacity }} className="card">
      <ImageWithFallback
        src={src}
        alt={`uploaded ${index}`}
        width="100"
        height="100"
        style={
          error?.length && !error[index]
            ? {
              padding: "0px",
              margin: "5px",
              border: "3px solid rgb(255 47 0)",
              borderRadius: "15px",
            }
            : {
              padding: "10px",
              margin: "5px",
              border: "3px solid rgb(78 176 39)",
              borderRadius: "15px",
            }
        }
        fallbackSrc={BlankImg}
      />
    </div>
  );
};

export const validateImageDimensions = (files) => {
  if (!Array.isArray(files)) {
    return Promise.resolve([]);
  }
  // "";
  return Promise.all(
    files.map((file) => {
      return new Promise((resolve) => {
        const img = new Image();

        if (typeof file === "string") {
          img.src = file;
        } else if (file instanceof Blob) {
          const reader = new FileReader();
          reader.onload = (event) => {
            img.src = event.target.result;
          };
          reader.readAsDataURL(file);
        } else {
          // "";
          resolve(false);
          return;
        }

        img.onload = () => {
          if (img.width >= 2048 && img.height >= 768) {
            // "";
            resolve(false);
          } else {
            resolve(true);
          }
        };

        img.onerror = () => {
          resolve(false);
        };
      });
    })
  );
};

export const validateProfileImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();

    if (typeof file === "string") {
      img.src = file;
    } else if (file instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (event) => {
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      resolve(false);
      return;
    }

    img.onload = () => {
      // "";
      if (img.width < 100 && img.height < 100) {
        resolve("Image must be at least 100x100");
      } else if (img.width > 500 && img.height > 500) {
        resolve("Image must be at most 500x500");
      } else {
        resolve(true);
      }
    };

    img.onerror = () => {
      resolve(false);
    };
  });
};

export const validateProjectLogoDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();

    if (typeof file === "string") {
      img.src = file;
    } else if (file instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (event) => {
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      resolve(false);
      return;
    }

    img.onload = () => {
      if (img.width < 250 || img.height < 50) {
        resolve("Image must be at least 250x50");
      } else if (img.width > 750 || img.height > 150) {
        resolve("Image must be at most 750x150");
      } else {
        resolve(true);
      }
    };

    img.onerror = () => {
      resolve(false);
    };
  });
};

export const handleImageUpload = (
  event,
  setFieldValue,
  currentValues,
  setImages,
  setImagesError = null
) => {
  if (setImagesError) {
    setImagesError([]);
  }
  const files = Array.from(event.target.files);
  const validFiles = files.filter((file) =>
    SUPPORTED_IMAGE_FORMATS.includes(file.type)
  );
  const imageUrls = validFiles.map((file) => URL.createObjectURL(file));
  setImages((prevImages) => [...prevImages, ...imageUrls]);
  const updatedFiles = [...currentValues.images, ...validFiles];
  setFieldValue("images", updatedFiles);
};

export const handleBrochureUpload = (
  event,
  setFieldValue,
  setBrochure,
  setFieldError
) => {
  const file = event.target.files[0];
  if (SUPPORTED_IMAGE_FORMATS.includes(file.type)) {
    setFieldError("brochure", "The file must be a PDF.");
    return;
  }
  setBrochure(URL.createObjectURL(file));
  setFieldValue("brochure", file);
};

export const handleImageRemove = async (
  index,
  setFieldValue,
  field,
  setValues,
  images,
  imagesInputRef = undefined,
  setError = undefined
) => {
  if (field === "project_logo" || field === "upload_plans") {
    setFieldValue(field, "");
    setValues(null);
    if (imagesInputRef !== undefined) {
      imagesInputRef.current.value = null;
    }
    return;
  } else {
    setFieldValue(
      field,
      images?.filter((_, i) => i !== index)
    );
  }

  setValues((prevImages) => {
    const updatedImages = prevImages.filter((_, i) => i !== index);
    // Perform the error check here after both state updates
    if (setError) {
      validateImageDimensions(updatedImages).then((isImagesValid) => {
        setError(isImagesValid);
      });
    }

    return updatedImages;
  });

  if (
    imagesInputRef !== undefined &&
    imagesInputRef?.current &&
    imagesInputRef?.current.files.length > 0
  ) {
    const dataTransfer = new DataTransfer();
    Array.from(imagesInputRef.current.files)
      .filter((_, i) => i !== index)
      .forEach((file) => dataTransfer.items.add(file));
    imagesInputRef.current.files = dataTransfer.files;
  }
};

export const handleFloorPlanUpload = (
  event,
  setFieldValue,
  currentValues,
  setFloorPlans,
  setFloorPlansError = null
) => {
  if (setFloorPlansError) {
    setFloorPlansError([]);
  }
  const files = Array.from(event.target.files);
  const validFiles = files.filter((file) =>
    SUPPORTED_IMAGE_FORMATS.includes(file.type)
  );
  const imageUrls = files.map((file) => URL.createObjectURL(file));

  setFloorPlans((prevImages) => [...prevImages, ...imageUrls]);
  const updatedFiles = [...currentValues.floor_plans, ...validFiles];
  setFieldValue("floor_plans", updatedFiles);
};

export const handleProjectLogoUpload = (
  event,
  setFieldValue,
  setImages,
  setFileError = null
) => {
  if (setFileError) {
    setFileError([]);
  }
  const file = event.target.files[0];
  const validFile = SUPPORTED_IMAGE_FORMATS.includes(file.type);
  if (validFile) {
    const imageUrl = URL.createObjectURL(file);
    setImages(imageUrl);
    setFieldValue("project_logo", file);
  }
};

export const handleDocumentUpload = (
  event,
  setFieldValue,
  currentValues,
  setDocument,
  setFieldError
) => {
  const file = event.target.files[0];
  // "";
  if (!SUPPORTED_PLAN_FORMATS.includes(file.type)) {
    setFieldError("upload_plans", "The file must be a JPEG or PNG.");
    return;
  }

  const imageUrls = URL.createObjectURL(file);

  setDocument(imageUrls);
  setFieldValue("upload_plans", file);
};

export const handleProjectPhotosUpload = (
  event,
  setFieldValue,
  currentValues,
  setProjectPhotos,
  setFileError = null
) => {
  if (setFileError) {
    setFileError([]);
  }
  const files = Array.from(event.target.files);
  const validFiles = files.filter((file) =>
    SUPPORTED_IMAGE_FORMATS.includes(file.type)
  );
  const imageUrls = validFiles.map((file) => URL.createObjectURL(file));
  setProjectPhotos((prevImages) => [...prevImages, ...imageUrls]);
  const updatedFiles = [...currentValues.project_photos, ...validFiles];
  setFieldValue("project_photos", updatedFiles);
};

export const handleCancelButton = (setRedirection, route) => {
  confirmAlert({
    title: "Confirm to Cancel",
    message: "Are you sure to discard this land.",
    buttons: [
      {
        label: "Discard",
        onClick: async () => {
          setRedirection(route);
        },
      },
      {
        label: "Cancel",
      },
    ],
  });
};

const validateFile = (file) => {
  return new Promise((resolve) => {
    // Check if the file is a URL string
    if (typeof file === "string") {
      resolve(true);
    } else if (file instanceof Blob) {
      // Handle Blob (either an image or a PDF)
      if (file.type === "application/pdf") {
        const maxFileSize = 10 * 1024 * 1024; // 10 MB in bytes
        if (file.size <= maxFileSize) {
          resolve(true); // PDF Blob is valid if it's less than 10 MB
          // "";
        } else {
          resolve(false); // PDF Blob is invalid due to large size
          // "";
        }
      } else {
        // Handle image Blob
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (event) => {
          img.src = event.target.result;
        };
        reader.readAsDataURL(file);
        img.onload = () => {
          if (img.width >= 2048 && img.height >= 768) {
            resolve(true); // Image dimensions are valid
          } else {
            resolve(false); // Image dimensions are invalid
            console.log("Image dimensions are invalid", [
              img.width,
              img.height,
            ]);
          }
        };

        img.onerror = () => {
          resolve(false); // Image failed to load
        };
      }
    } else {
      resolve(false); // If the file is neither a string nor a Blob, resolve as false
    }
  });
};

export const validateImageAndPDFDimensions = (files) => {
  if (!Array.isArray(files)) {
    return Promise.resolve([]);
  }

  return Promise.all(files.map((file) => validateFile(file)));
};

export const handleStatementInformationUpload = (
  event,
  setFieldValue,
  currentValues,
  setDocument,
  setFieldError
) => {
  const file = event.target.files[0];
  // "";
  if (!SUPPORTED_PLAN_FORMATS.includes(file.type)) {
    setFieldError(event.target.name, "The file must be a PDF or JPEG/PNG.");
    return;
  }

  const imageUrls = URL.createObjectURL(file);

  setDocument([imageUrls]);
  setFieldValue(event.target.name, [file]);
};

export const getIsFavourite = (properties = [], id) => {
  // ""
  if (properties.length && id) {
    if (properties.includes(parseInt(id))) {
      return true;
    }
  }
  return false;
};

export const showPrice = (price) => {
  if (price) {
    let newPrice = parseInt(price) || 0;
    return `$${newPrice.toLocaleString()}`;
  } else {
    return `$0`;
  }
};

export const formatLandSize = (landSize, unit) => {
  if (landSize) {
    let newLandSize = parseInt(landSize) || 0;
    return `${newLandSize.toLocaleString()}${unit}`;
  } else {
    return `0${unit}`;
  }
};

export const formatCurrency = (value) => {
  return value ? parseFloat(value).toLocaleString() : "";
};

export const handlePriceChange = (event, setFieldValue) => {
  const value = event.target.value;
  // Update the form field value without the currency symbol
  const numericValue = value.replace(/[^0-9.]/g, "");
  setFieldValue(event.target.name, numericValue);
};

// export const handleUrlParams = (input) => {
//   let temp = input.replace(/-/g, ' ');
//   "";

//   return temp
// }
export const handleUrlParams = (input) => {
  let temp = input.replace(/ /g, "TEMP_PLACEHOLDER");
  temp = temp.replace(/-/g, " ");
  return temp.replace(/TEMP_PLACEHOLDER/g, "-");
};

export const INITIAL_CONFIRM_MODEL = {
  isOpen: false,
  onClose: null,
  onConfirm: () => { },
  onCancel: () => { },
  title: ``,
  description: ``,
  cancelText: `Cancel`,
  confirmText: `Confirm`,
}